import { DeleteOutline, InfoOutlined, LoopOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Card, Stack, styled } from '@mui/material';
import {
  useDeleteGiftContributorRecurring,
  useGetGiftContributorRecurring
} from '@portal-middleware/hooks';
import { formatCurrency } from '@vestwell-frontend/helpers';
import { useNotifications, useToggle } from '@vestwell-frontend/hooks';
import { GiftIcon, hexToRgba, Text } from '@vestwell-frontend/ui';

import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useGiftStateContext } from '../contexts/GiftStateContext';
import { getNextTimeGiftWillProcess } from '../helpers/formatGiftFrequency';
import { canGiftAccountRecieveGifts } from '../helpers/giftAccountHelper';

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: hexToRgba(theme.palette.cornflowerBlue.main, 0.05),
  borderRadius: theme.spacing(8),
  display: 'inline-block',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(12),
  padding: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(0)
  }
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(0)
}));

const StyledGiftIcon = styled(GiftIcon)(() => ({
  alignSelf: 'center'
}));

const RecurringGiftsList: FC = () => {
  const { giftingId } = useParams();

  const notify = useNotifications();
  const [canRecieveGifts, setCanRecieveGifts] = useToggle(true);

  const { giftContributorId, giftAndAccount } = useGiftStateContext();

  const recurringGifts = useGetGiftContributorRecurring(
    giftContributorId,
    giftingId,
    {
      query: {
        suspense: true
      }
    }
  );

  const { mutate: deleteRecurringGift } = useDeleteGiftContributorRecurring({
    mutation: {
      onError: () => {
        notify.error('Failed to delete recurring gift');
      },
      onSuccess: () => {
        recurringGifts.refetch();
        notify.success('Your recurring gift has been deleted.');
      }
    }
  });

  useEffect(() => {
    setCanRecieveGifts(canGiftAccountRecieveGifts(giftAndAccount?.data?.data));
  }, [giftAndAccount]);

  const handleDelete = (giftId: string) => {
    deleteRecurringGift({
      giftAccountId: giftingId,
      giftContributorId: giftContributorId,
      recurringId: giftId
    });
  };

  return (
    <>
      <StyledBox>
        <LoopOutlined color='cornflowerBlue' />
      </StyledBox>
      <Stack>
        <Text aria-level={2} variant='f2'>
          Recurring Gifts
        </Text>
        {!canRecieveGifts && recurringGifts?.data?.data?.length > 0 && (
          <StyledAlert
            icon={<InfoOutlined fontSize='small' />}
            role='note'
            severity='info'>
            <Text color='info' mb={0} variant='i1'>
              If the recipient can receive a gift at the time of your scheduled
              recurring gift, the contribution will be submitted. Otherwise, the
              recurring gift will not be submitted.
            </Text>
          </StyledAlert>
        )}
        <Stack gap={4}>
          {recurringGifts?.data?.data?.length === 0 && (
            <Text>No recurring gifts are setup</Text>
          )}
          {recurringGifts?.data?.data?.map(gift => {
            return (
              <Card key={gift.id}>
                <Stack
                  flexDirection='row'
                  gap={2}
                  justifyContent='space-between'>
                  <Stack flexDirection='row' gap={2}>
                    <StyledGiftIcon color='cornflowerBlue' />
                    <Stack>
                      <Text fontWeight={800} mb={0}>
                        {formatCurrency(gift.amount)} {gift.parameters.occasion}{' '}
                        Gift
                      </Text>
                      <Text mb={0} variant='i1'>
                        Your next {formatCurrency(gift.amount)} gift is
                        scheduled for{' '}
                        {getNextTimeGiftWillProcess(gift.frequency)}.
                      </Text>
                    </Stack>
                  </Stack>
                  <Button onClick={() => handleDelete(gift.id)} variant='text'>
                    <DeleteOutline />
                  </Button>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    </>
  );
};

export default RecurringGiftsList;
